import { useState } from 'react';
import { Link } from 'react-router-dom';
import { postApi } from 'views/services/api';
import { toast } from 'react-toastify';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Alert,
  Collapse,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Stack,
  LinearProgress
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';

// assets
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ============================|| HOTEL PMS - LOGIN COMPONENT PROFESIONAL ||============================ //

const HotelLogin = ({ ...others }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setUserData] = useState();
  const [loginError, setLoginError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <Collapse in={loginError}>
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          onClose={() => setLoginError(false)}
          sx={{
            mb: 3,
            borderRadius: '8px',
            backgroundColor: theme.palette.error.light + '20',
            color: theme.palette.error.dark,
            border: '1px solid',
            borderColor: theme.palette.error.light + '50',
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

      <Formik
        initialValues={{
          email: '',
          password: ''
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string().email('Debe ser un correo electrónico válido').max(255).required('El correo electrónico es requerido'),
          password: Yup.string().max(255).required('La contraseña es requerida')
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          setIsSubmitting(true);
          setLoginError(false);

          try {
            const response = await postApi(values.email === 'admin@gmail.com' ? 'api/user/login' : 'api/hotel/login', values);

            setUserData(response.data.user);
            localStorage.setItem('userData', JSON.stringify(response.data.user));

            if (response.status === 200) {
              // Si "Recordarme" está marcado, guardar el email
              if (rememberMe) {
                localStorage.setItem('rememberedEmail', values.email);
              } else {
                localStorage.removeItem('rememberedEmail');
              }

              toast.success('Bienvenido al sistema', {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
                icon: <CheckCircleOutlineIcon />
              });

              localStorage.setItem('token', response.data.token);
              const role = response.data.user.role;

              // Pequeña demora para mejor UX
              setTimeout(() => {
                if (role === 'admin') {
                  localStorage.setItem('hotelData', JSON.stringify(response.data.user));
                  window.location.replace('/dashboard/admindashboard');
                } else if (role === 'hotelAdmin') {
                  localStorage.setItem('hotelData', JSON.stringify(response.data.user));
                  window.location.replace('/dashboard/app');
                } else {
                  localStorage.setItem('hotelData', JSON.stringify(response.data.user));
                  window.location.replace('/dashboard/app');
                }
              }, 1000);
            } else {
              setLoginError(true);
              setErrorMessage(response.response.data.error || 'Error al iniciar sesión. Verifica tus credenciales.');
              toast.error(response.response.data.error || 'Error al iniciar sesión');
            }
          } catch (error) {
            console.error('Login error:', error);
            setLoginError(true);
            setErrorMessage(error.response?.data?.error || 'No se pudo conectar con el servidor. Por favor, intenta más tarde.');
            toast.error(error.response?.data?.error || 'Error de conexión');
          } finally {
            setIsSubmitting(false);
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <Stack spacing={3}>
              {/* Campo de Email */}
              <FormControl fullWidth error={Boolean(touched.email && errors.email)}>
                <InputLabel htmlFor="outlined-adornment-email-login">Correo Electrónico / Usuario</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-email-login"
                  type="email"
                  value={values.email}
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  label="Correo Electrónico / Usuario"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.grey[300]
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
                {touched.email && errors.email && (
                  <FormHelperText error id="standard-weight-helper-text-email-login">
                    {errors.email}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Campo de Contraseña */}
              <FormControl fullWidth error={Boolean(touched.password && errors.password)}>
                <InputLabel htmlFor="outlined-adornment-password-login">Contraseña</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password-login"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="large"
                        sx={{
                          color: theme.palette.grey[500],
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: theme.palette.primary.main
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Contraseña"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.grey[300]
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
                {touched.password && errors.password && (
                  <FormHelperText error id="standard-weight-helper-text-password-login">
                    {errors.password}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Recordarme y Olvidé contraseña */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: -1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      name="rememberMe"
                      size="small"
                      sx={{
                        color: theme.palette.grey[500],
                        '&.Mui-checked': {
                          color: theme.palette.primary.main
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Recordar mis datos
                    </Typography>
                  }
                />

                <Typography
                  component={Link}
                  to="/forgot-password"
                  variant="body2"
                  color="primary"
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                      color: theme.palette.primary.dark
                    }
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Typography>
              </Stack>

              {/* Progress Bar cuando está cargando */}
              {isSubmitting && (
                <Box sx={{ width: '100%', mt: -2 }}>
                  <LinearProgress
                    sx={{
                      height: 2,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Box>
              )}

              {errors.submit && (
                <Box>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Box>
              )}

              {/* Botón de Login */}
              <Box>
                <AnimateButton>
                  <Button
                    disableElevation
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      padding: '12px 0',
                      backgroundColor: theme.palette.primary.main,
                      boxShadow: 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      },
                      '&:disabled': {
                        backgroundColor: theme.palette.grey[300],
                        color: theme.palette.grey[500]
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                        <span>Verificando credenciales...</span>
                      </Stack>
                    ) : (
                      'Iniciar Sesión'
                    )}
                  </Button>
                </AnimateButton>
              </Box>
            </Stack>
          </form>
        )}
      </Formik>
    </>
  );
};

export default HotelLogin;
